import React from 'react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Shield, Map, CheckCircle, FileText, Globe, AlertTriangle } from 'lucide-react';

const LogisticsTransport = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950">
            <Navbar />
            <main className="pt-24 pb-16">
                {/* Hero Section */}
                <section className="py-20 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 blur-3xl" />
                    <div className="container mx-auto px-4 relative z-10">
                        <div className="max-w-4xl mx-auto text-center">
                            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2 mb-6">
                                <Truck className="w-5 h-5 text-blue-400" />
                                <span className="text-blue-300 font-medium text-sm">Professional Truck Drivers</span>
                            </div>
                            <h1 className="text-5xl md:text-7xl font-black text-white mb-6">
                                Logistics & <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-500">Transport</span>
                            </h1>
                            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
                                Become a Professional Truck Driver in Germany. We guide you through the complete process for foreign workers, including license conversions, work permits, and finding the right employer.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all"
                            >
                                Apply Now
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Basic Requirements */}
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            Basic Requirements to Be Eligible
                        </h2>
                        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all">
                                <div className="text-4xl mb-4">🚀</div>
                                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-blue-400" /> Driving License
                                </h3>
                                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                                    You must have a valid heavy vehicle licence — in EU terms: <strong>Category C or CE</strong> (for trucks with trailers).
                                </p>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    If you have an Indian Heavy Motor Vehicle (HMV) licence, it will likely need conversion or you'll have to pass German theory & practical tests at a driving school (Fahrschule).
                                </p>
                            </div>
                            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all">
                                <div className="text-4xl mb-4">📜</div>
                                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-blue-400" /> Code 95 / Driver CPC
                                </h3>
                                <p className="text-slate-400 text-sm leading-relaxed mb-4">
                                    All professional truck drivers need a Code 95 qualification (Driver Certificate of Professional Competence), a mandatory EU standard certificate.
                                </p>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    It typically includes ~140 hours of training and tests in safety and professional driving.
                                </p>
                            </div>
                            <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all">
                                <div className="text-4xl mb-4">🧰</div>
                                <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-blue-400" /> Experience & More
                                </h3>
                                <ul className="space-y-3 text-sm text-slate-400">
                                    <li className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                                        <span>Employers usually want minimum <strong>3 years</strong> of heavy driving experience.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                                        <span>A clean police/traffic record and medical fitness test are required.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                                        <span>Some basic German (A2–B1) helps with training and job performance.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Important Technical Points */}
                <section className="py-16 bg-slate-800/30">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto bg-blue-900/20 border border-blue-500/30 rounded-2xl p-8">
                            <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                <AlertTriangle className="w-6 h-6 text-amber-400" /> Important Technical Points
                            </h3>
                            <h4 className="text-lg font-bold text-blue-300 mb-2">🚦 License Conversion</h4>
                            <p className="text-slate-300 mb-4">
                                Non-EU licences usually cannot be directly exchanged for a German CE licence — you'll have to pass German tests (theory + practical).
                            </p>
                            <p className="text-slate-300">
                                Some employers may support your training process by letting you work in other roles (e.g., co-driver, logistics assistant) while you train.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Recruitment Process */}
                <section className="py-16">
                    <div className="container mx-auto px-4 max-w-5xl">
                        <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-12">
                            How the Recruitment Process Works
                        </h2>
                        <div className="space-y-6">
                            {[
                                {
                                    step: '1️⃣',
                                    title: 'Find a Job with a German Employer',
                                    desc: 'You cannot apply for a work visa without a formal job offer from a licensed German transport company. Employers often provide you with an employment contract and help with formalities like driver qualification, Code 95, and visa docs.'
                                },
                                {
                                    step: '2️⃣',
                                    title: 'Work Permit Approval (Federal Employment Agency)',
                                    desc: 'German employers must get approval from the Federal Employment Agency (BA). The agency checks job conditions like salary/hours and ensures the contract is realistic. Some employers allow you to work and train at the same time if you are missing qualifications.'
                                },
                                {
                                    step: '3️⃣',
                                    title: 'Apply for a German Employment Visa (D-Visa)',
                                    desc: 'Once you have a job offer and BA approval, apply for a National D-Visa. Visa decisions usually take 4-8 weeks. Required docs: Valid passport, signed contract, BA approval, accommodation proof, medical insurance, driver\'s license/qualifications, and PCC.'
                                },
                                {
                                    step: '4️⃣',
                                    title: 'Arrival & Residence Permit',
                                    desc: 'After arriving in Germany, you must register your address (Anmeldung) at the local registration office, then convert the visa to a Residence Permit for Employment (Aufenthaltserlaubnis).'
                                }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-6 bg-slate-800/50 p-6 rounded-2xl border border-slate-700 hover:border-blue-500/50 transition-all">
                                    <div className="text-3xl shrink-0 mt-1">{item.step}</div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                        <p className="text-slate-400">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Summary Checklist */}
                <section className="py-16 bg-slate-800/30">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl font-black text-white text-center mb-12">
                            Summary Checklist ✅
                        </h2>
                        <div className="max-w-3xl mx-auto bg-slate-800/80 p-8 rounded-3xl border border-slate-700">
                            <div className="grid sm:grid-cols-2 gap-4">
                                {[
                                    'Valid Heavy Licence (or pathway to get one in Germany)',
                                    'Code 95 Professional Qualification',
                                    'Job offer from German employer',
                                    'Work permit (BA approval)',
                                    'Employment visa (D-Visa)',
                                    'Convert to residence permit after arrival'
                                ].map((item, i) => (
                                    <div key={i} className="flex items-start gap-3 bg-slate-900/50 p-4 rounded-xl">
                                        <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                                        <span className="text-slate-300 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto text-center bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-3xl p-12">
                            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                                Ready to Drive Your Career Forward?
                            </h2>
                            <p className="text-slate-300 mb-8">
                                Connect with German transport companies and get the support you need to navigate the licensing and visa process.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-[0_0_30px_rgba(59,130,246,0.5)] transition-all"
                            >
                                Start Application
                                <ArrowRight className="w-5 h-5" />
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default LogisticsTransport;
